/**
 * React class ErrorBoundary that dispatches ACTION_LOG_ERROR to the Nuvemshop
 * admin when a component tree throws. Required for App Store homologation.
 *
 * Renders null on error (the nexo admin shell shows a generic error message).
 * Wrap the whole app tree with this component.
 */

import type { NexoClient } from '@tiendanube/nexo';
import { ACTION_LOG_ERROR } from '@tiendanube/nexo';
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  nexo: NexoClient;
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Dispatch the error to the Nuvemshop admin shell for logging.
    this.props.nexo.dispatch({
      type: ACTION_LOG_ERROR,
      payload: {
        message: error.message,
        stack: error.stack ?? '',
        componentStack: errorInfo.componentStack ?? '',
        url: window.location.href,
      },
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Render nothing - nexo admin handles the error UI at the shell level.
      return null;
    }
    return this.props.children;
  }
}
